import { getInlineChoiceMenu } from '../getters/inlineChoiceGetter';
import {
  buildBarred,
  buildFraction,
  buildFunction,
  buildMulti,
  buildPower,
  buildQuantity,
  buildRoot,
  buildSubscript,
  buildText,
  getBox,
  Line,
  MathObject,
  Multi,
  Text,
} from '../mathObjects';
import { MAX_MULTI_LINE_WIDTH } from '../problemCanvas/problemCanvasConstants';
import type { ProblemJSDependencies } from '../problemJSDependencies';
import { getReadAloudButtonWidth, lineFontSize } from '../state';

import { decodeText } from './decodeText';
import {
  applySpanFormatToDirectChild,
  extractSpanAttributes,
} from './spanFormat';

export const TAGS = {
  TEXT: 't',
  QUANTITY: 'q',
  ABSOLUTE_VALUE: 'a',
  ROOT: 'r',
  RAD: 'rad',
  RAD_INDEX: 'root',
  FRACTION: 'f',
  NUMERATOR: 'num',
  DENOMINATOR: 'denom',
  BARRED: 'b',
  LINETYPE: 'linetype',
  POWER: 'p',
  BASE: 'b',
  EXPONENT: 'e',
  SUBSCRIPT: 's',
  SUBSCRIPT_BASE: 'b',
  SUBSCRIPT_SUB: 'sub',
  FUNCTION: 'tf',
  FUNCTION_TYPE: 'ft',
  FUNCTION_BASE: 'fb',
  FUNCTION_VALUE: 'v',
  INLINE_CHOICE: 'inlineChoice',
  SPAN: 'span',
  BOX: 'box',
  LCHAR: 'lChar',
  CONTENTS: 'contents',
  RCHAR: 'rChar',
} as const;

export type Tag = typeof TAGS[keyof typeof TAGS];

const attributeBearingTags = new Set<Tag>([TAGS.SPAN]);

const isOpeningTagFor = (tag: Tag, afterOpen: string): boolean => {
  if (afterOpen.startsWith(tag + '>')) {
    return true;
  }

  return attributeBearingTags.has(tag) && afterOpen.startsWith(tag + ' ');
};

const findClose = (tag: Tag, start: number, val: string): number => {
  let depth = 0;
  let pos = start;

  for (
    let nextOpen = val.indexOf('<', pos);
    nextOpen !== -1;
    nextOpen = val.indexOf('<', pos)
  ) {
    const afterOpen = val.substring(nextOpen + 1);

    // opening tag; attributes are only valid on tags listed in attributeBearingTags
    if (isOpeningTagFor(tag, afterOpen)) {
      depth++;
      pos = nextOpen + 1;
      continue;
    }

    // closing tag: </span>
    if (afterOpen.startsWith('/' + tag + '>')) {
      if (depth === 0) return nextOpen;
      depth--;
      pos = nextOpen + 1;
      continue;
    }

    // not relevant
    pos = nextOpen + 1;
  }

  throw new Error(`Unclosed tag <${tag}>`);
};

interface Props {
  problemJS: ProblemJSDependencies;
  line: Line;
  val: string;
  first: number;
  last: number;
  multiline?: boolean;
  maxTextW?: number;
  gettingBase?: boolean;
}

export const parseXML = ({
  problemJS,
  line,
  val,
  first,
  last,
  multiline,
  maxTextW,
  gettingBase,
}: Props): Multi => {
  const mtList: MathObject[] = [];

  let pos = first;

  while (pos < last) {
    const openTagAt = val.indexOf('<', pos);

    if (openTagAt === -1 || openTagAt >= last) break;

    const closeTagAt = val.indexOf('>', openTagAt);

    if (closeTagAt === -1) break;

    // string between < and >, such as 'num' or 'span style=bold'
    const tagContents = val.substring(openTagAt + 1, closeTagAt);
    // fancy regex finds index of the first whitespace or the end of the string, whichever comes first
    const tag = tagContents.split(/\s+/)[0] as Tag;

    const contentStart = closeTagAt + 1;
    const closeTagPos = findClose(tag, contentStart, val);
    const contentEnd = closeTagPos - 1;

    if (tag === TAGS.SPAN) {
      const format = extractSpanAttributes(tagContents);

      const innerMulti = parseXML({
        problemJS,
        line,
        val,
        first: contentStart,
        last: contentEnd,
        multiline,
        gettingBase,
      });

      // Flatten span by pushing inner children. Ordinary styles cascade into
      // descendants; slash wraps each directly wrapped meaningful object so
      // grouped values draw one cancellation mark instead of one per child or
      // parser-injected empty spacer.
      innerMulti.children.forEach(child => {
        mtList.push(applySpanFormatToDirectChild(child as MathObject, format));
      });

      pos = val.indexOf('>', closeTagPos) + 1;
      continue;
    } else if (tag === TAGS.TEXT) {
      if (contentStart <= contentEnd) {
        let text = val.substring(contentStart, contentEnd + 1);
        const decoded = decodeText(text);

        if (decoded) text = decoded;

        let bestW =
          maxTextW ?? Math.min(MAX_MULTI_LINE_WIDTH, window.innerWidth - 10);

        // If the caller supplies maxTextW, then it is responsible for accounting for the read aloud button if present.
        // (We were double-subtracting: once in SAVP and once here)
        if (!maxTextW && problemJS.isReadAloud()) {
          bestW -= getReadAloudButtonWidth();
        }

        const txt = buildText(
          problemJS,
          0,
          0,
          text,
          lineFontSize,
          multiline,
          bestW,
          maxTextW === undefined // clampToScreenMax only if maxTextW not supplied
        );

        mtList.push(txt);
      }
    } else if (tag === TAGS.QUANTITY || tag === TAGS.ABSOLUTE_VALUE) {
      let obj: MathObject;
      const absoluteValue = tag === TAGS.ABSOLUTE_VALUE;
      const quantityBody = val
        .substring(contentStart, contentEnd + 1)
        .trimStart();
      const contentsTagIndex = quantityBody.indexOf(`<${TAGS.CONTENTS}>`);
      const rCharTagIndex = quantityBody.indexOf(`<${TAGS.RCHAR}>`);
      const isExplicitQuantity =
        !absoluteValue &&
        quantityBody.startsWith(`<${TAGS.LCHAR}>`) &&
        contentsTagIndex > 0 &&
        rCharTagIndex > contentsTagIndex;

      if (contentStart > contentEnd) {
        obj = buildQuantity(problemJS, line, undefined, absoluteValue);
      } else if (isExplicitQuantity) {
        // Only this quantity's own wrapper should use the explicit-delimiter
        // path. Nested grouped content inside <contents> can also contain
        // <lChar>/<contents>/<rChar>, and treating that as the outer wrapper
        // sends the parser back into the same substring indefinitely.
        const lCharStart =
          val.indexOf(`<${TAGS.LCHAR}>`, openTagAt) + TAGS.LCHAR.length + 2;
        const lCharEnd = findClose(TAGS.LCHAR, lCharStart, val) - 1;
        const lchar = parseXML({
          problemJS,
          line,
          val,
          first: lCharStart,
          last: lCharEnd,
        }).children[0] as Text;

        const contentsStart =
          val.indexOf(`<${TAGS.CONTENTS}>`, lCharEnd) +
          TAGS.CONTENTS.length +
          2;
        const contentsEnd = findClose(TAGS.CONTENTS, contentsStart, val) - 1;
        const contents = parseXML({
          problemJS,
          line,
          val,
          first: contentsStart,
          last: contentsEnd,
        });

        const rCharStart =
          val.indexOf(`<${TAGS.RCHAR}>`, contentsEnd) + TAGS.RCHAR.length + 2;
        const rCharEnd = findClose(TAGS.RCHAR, rCharStart, val) - 1;
        const rchar = parseXML({
          problemJS,
          line,
          val,
          first: rCharStart,
          last: rCharEnd,
        }).children[0] as Text;

        obj = buildQuantity(
          problemJS,
          line,
          contents,
          absoluteValue,
          lchar,
          rchar
        );
      } else {
        const contents = parseXML({
          problemJS,
          line,
          val,
          first: contentStart,
          last: contentEnd,
        });

        obj = buildQuantity(problemJS, line, contents, absoluteValue);
      }

      mtList.push(obj);
    } else if (tag === TAGS.ROOT) {
      let root: MathObject;

      if (contentStart > contentEnd) {
        root = buildRoot(problemJS, line);
      } else if (val.includes(`<${TAGS.RAD}>`, openTagAt)) {
        const radStart =
          val.indexOf(`<${TAGS.RAD}>`, openTagAt) + TAGS.RAD.length + 2;
        const radEnd = findClose(TAGS.RAD, radStart, val) - 1;
        const rad = parseXML({
          problemJS,
          line,
          val,
          first: radStart,
          last: radEnd,
        });

        const indexStart =
          val.indexOf(`<${TAGS.RAD_INDEX}>`, radEnd) +
          TAGS.RAD_INDEX.length +
          2;
        const indexEnd = findClose(TAGS.RAD_INDEX, indexStart, val) - 1;
        const index = parseXML({
          problemJS,
          line,
          val,
          first: indexStart,
          last: indexEnd,
        });

        root = buildRoot(problemJS, line, rad, index);
      } else {
        const content = parseXML({
          problemJS,
          line,
          val,
          first: contentStart,
          last: contentEnd,
        });

        root = buildRoot(problemJS, line, content);
      }

      mtList.push(root);
    } else if (tag === TAGS.FRACTION) {
      const frac = buildFraction(problemJS, line);

      if (contentStart <= contentEnd) {
        const numStart =
          val.indexOf(`<${TAGS.NUMERATOR}>`, openTagAt) +
          TAGS.NUMERATOR.length +
          2;
        const numEnd = findClose(TAGS.NUMERATOR, numStart, val) - 1;
        const num = parseXML({
          problemJS,
          line,
          val,
          first: numStart,
          last: numEnd,
        });

        const denomStart =
          val.indexOf(`<${TAGS.DENOMINATOR}>`, numEnd) +
          TAGS.DENOMINATOR.length +
          2;
        const denomEnd = findClose(TAGS.DENOMINATOR, denomStart, val) - 1;
        const denom = parseXML({
          problemJS,
          line,
          val,
          first: denomStart,
          last: denomEnd,
        });

        frac.setNumeratorAndDenominator(num, denom);
      }

      mtList.push(frac);
    } else if (tag === TAGS.BARRED) {
      const barred = buildBarred(problemJS, line);

      if (contentStart <= contentEnd) {
        let lineType = 'LINE';
        let stuffStart = contentStart;

        // <b><linetype>DASH</linetype> ...stuff... </b>
        const linetypeOpen = val.indexOf(`<${TAGS.LINETYPE}>`, contentStart);

        if (linetypeOpen !== -1 && linetypeOpen < closeTagPos) {
          const ltValueStart = linetypeOpen + `<${TAGS.LINETYPE}>`.length;
          const ltCloseTagPos = findClose(TAGS.LINETYPE, ltValueStart, val);

          lineType = val.substring(ltValueStart, ltCloseTagPos);

          // Move stuffStart to the character after </linetype>
          const afterLtClose = val.indexOf('>', ltCloseTagPos);

          if (afterLtClose !== -1) {
            stuffStart = afterLtClose + 1;
          }
        }

        const stuff = parseXML({
          problemJS,
          line,
          val,
          first: stuffStart,
          last: contentEnd,
        });

        barred.setLineType(lineType);
        barred.setStuff(stuff);
      }

      mtList.push(barred);
    } else if (tag === TAGS.POWER) {
      const power = buildPower(problemJS, line);

      if (contentStart <= contentEnd) {
        const baseStart =
          val.indexOf(`<${TAGS.BASE}>`, openTagAt) + TAGS.BASE.length + 2;
        const baseEnd = findClose(TAGS.BASE, baseStart, val) - 1;
        const base = parseXML({
          problemJS,
          line,
          val,
          first: baseStart,
          last: baseEnd,
          gettingBase: true,
        });

        const expStart =
          val.indexOf(`<${TAGS.EXPONENT}>`, baseEnd) + TAGS.EXPONENT.length + 2;
        const expEnd = findClose(TAGS.EXPONENT, expStart, val) - 1;
        const exp = parseXML({
          problemJS,
          line,
          val,
          first: expStart,
          last: expEnd,
        });

        power.setBaseAndExp(base, exp);
      }

      mtList.push(power);
    } else if (tag === TAGS.SUBSCRIPT) {
      const sub = buildSubscript(problemJS, line);

      if (contentStart <= contentEnd) {
        const baseStart =
          val.indexOf(`<${TAGS.SUBSCRIPT_BASE}>`, openTagAt) +
          TAGS.SUBSCRIPT_BASE.length +
          2;
        const baseEnd = findClose(TAGS.SUBSCRIPT_BASE, baseStart, val) - 1;
        const base = parseXML({
          problemJS,
          line,
          val,
          first: baseStart,
          last: baseEnd,
        });

        const subStart =
          val.indexOf(`<${TAGS.SUBSCRIPT_SUB}>`, baseEnd) +
          TAGS.SUBSCRIPT_SUB.length +
          2;
        const subEnd = findClose(TAGS.SUBSCRIPT_SUB, subStart, val) - 1;
        const subscript = parseXML({
          problemJS,
          line,
          val,
          first: subStart,
          last: subEnd,
        });

        sub.setBaseAndSub(base, subscript);
      }

      mtList.push(sub);
    } else if (tag === TAGS.FUNCTION) {
      const fn = buildFunction(problemJS, line);

      if (contentStart <= contentEnd) {
        const typeStart =
          val.indexOf(`<${TAGS.FUNCTION_TYPE}>`, openTagAt) +
          TAGS.FUNCTION_TYPE.length +
          2;
        const typeEnd = findClose(TAGS.FUNCTION_TYPE, typeStart, val) - 1;
        const type = parseXML({
          problemJS,
          line,
          val,
          first: typeStart,
          last: typeEnd,
        });

        const baseStart =
          val.indexOf(`<${TAGS.FUNCTION_BASE}>`, typeEnd) +
          TAGS.FUNCTION_BASE.length +
          2;
        const baseEnd = findClose(TAGS.FUNCTION_BASE, baseStart, val) - 1;
        const base = parseXML({
          problemJS,
          line,
          val,
          first: baseStart,
          last: baseEnd,
        });

        const valStart =
          val.indexOf(`<${TAGS.FUNCTION_VALUE}>`, baseEnd) +
          TAGS.FUNCTION_VALUE.length +
          2;
        const valEnd = findClose(TAGS.FUNCTION_VALUE, valStart, val) - 1;
        const value = parseXML({
          problemJS,
          line,
          val,
          first: valStart,
          last: valEnd,
        });

        fn.setAll(type, base, value);
      }

      mtList.push(fn);
    } else if (tag === TAGS.INLINE_CHOICE) {
      mtList.push(
        getInlineChoiceMenu(
          problemJS,
          val.substring(contentStart, contentEnd + 1),
          problemJS.attemptGetters
        )
      );
    } else if (tag === TAGS.BOX) {
      mtList.push(getBox());
    } else {
      console.error('Unsupported tag:', tag);
    }

    // Move past closing tag
    pos = val.indexOf('>', closeTagPos) + 1;
  }

  // ----------------------------------------------------------------------
  // Build final Multi object
  // ----------------------------------------------------------------------
  if (mtList.length === 0) {
    return buildMulti(problemJS, line);
  }

  const result = buildMulti(problemJS, line, undefined, maxTextW);

  result.locationParent = line;

  if (gettingBase && mtList.length === 1 && mtList[0].t === 'quantity') {
    result.children.splice(0, 1); // remove default empty text
    result.add(mtList[0]);
  } else {
    mtList.forEach(obj => {
      result.add(obj);
      obj.locationParent = result;
    });
    result.balanceEmptyTexts();
  }

  return result;
};
